<template>
  <div>
    <v-responsive class="mx-auto overflow-visible">
      <div>
        <v-card>
          <v-card-title color="primary">{{ $t('Project information') }}</v-card-title>
          <v-card-text>
              <project-basic-info :project="project"/>
          </v-card-text>
        </v-card>
        <v-form
          ref="formProject"
          v-model="valid"
          lazy-validation
        >
          <v-card class="mt-5">
            <v-card-title color="primary">{{ $t('Assignment') }}</v-card-title>
            <v-card-text>
              <alert/>
              <v-simple-table>
                <template v-slot:default>
                    <thead>
                    <tr>
                        <th> # </th>
                        <th>{{ $t('Weight') }} </th>
                        <th>{{ $t('Name') }} </th>
                        <th>{{ $t('Assign to') }} </th>
                        <th>{{ $t('Due date') }} </th>
                        <th>{{ $t('Status') }} </th>
                    </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(item, index) in form.tasks"
                            :key="`task-${index}`"
                            class="py-3"
                        >
                            <td>{{ index + 1 }}</td>
                            <td>
                              <div v-if="isShow">
                                {{ item.weight }}
                              </div>
                              <div v-else>
                                <v-text-field
                                  dense
                                  type="number"
                                  v-model.number="item.weight"
                                  :disabled="item.status !== 'reviewed'"
                                  :rules="rules.weight"
                                  hide-details="auto"
                                ></v-text-field>
                              </div>
                            </td>
                            <td>
                              <router-link :to="{ name: 'my-workings.detail', params: {id: item.id} }">{{ item.name }}</router-link>
                            </td>
                            <td>{{ item.user.name || '---' }}</td>
                            <td>{{ dateFormat(item.due_date) }}</td>
                            <td>{{ __attr(item.status) }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                      <th/>
                      <th align="left" colspan="5">
                        {{ $t('Total weight') }}: <span class="primary--text">{{ totalWeight }}</span>
                        <div v-if="totalWeight !== maxWeight" class="font-italic font-weight-medium red--text">
                          {{ $t('The total weight should be') }} {{ maxWeight }}
                        </div>
                      </th>
                    </tfoot>
                </template>
              </v-simple-table>
              <div class="mt-4" v-if="!isShow">
                <v-textarea
                  dense
                  outlined
                  v-model="form.result_summary"
                  :rules="rules.result_summary"
                  hide-details="auto"
                  rows="3"
                  :label="$t('Result summary')"
                  :placeholder="$t('Enter your content')"
                  :disabled="!canSubmit"
                />
              </div>
              <div class="mt-4" v-else>
                <label>{{ $t('Result summary')}}</label>
                <p class="mb-0">{{ project.result_summary }}</p>
              </div>
            </v-card-text>
          </v-card>
        </v-form>
      </div>
    </v-responsive>
    <div class="text-center my-5">
      <v-btn color="secondary" @click="__back('project-processes.index', $event)" class="mr-3">{{ $t('Back') }}</v-btn>
      <v-btn color="primary" v-if="!isShow" :disabled="(disabled || !canSubmit)" @click="submit">{{ $t('Submit') }}</v-btn>
    </div>
  </div>
</template>

<script>
import { get, call } from 'vuex-pathify'
import { dateFormat, utcToLocalDateTimeLess } from '@utils'
import ProjectBasicInfo from '@components/ProjectBasicInfo'
import Alert from '@components/Alert'
import Validation from '@utils/validation'

export default {
  name: 'ProjectProcessShow',
  components: {
    ProjectBasicInfo,
    Alert
  },
  data: () => ({
    valid: true,
    disabled: true,
    rules: {
      weight: [
        (v) => Validation.required(v),
        (v) => Validation.number(v),
        (v) => Validation.min(v, 0)
      ],
      result_summary: [
        (v) => Validation.required(v)
      ]
    },
    form: {
      result_summary: '',
      tasks: []
    }
  }),
  computed: {
    project: get('project/project'),
    userId: get('user/id'),

    projectId() {
      return this.$route.params.id
    },

    isShow() {
      return ['completed', 'reviewed'].includes(this.project.status)
    },

    canSubmit() {
      return this.form.tasks.length && (this.form.tasks.length === (this.form.tasks.filter(function(item) {
        return item.status === 'reviewed'
      })).length)
    },

    totalTasks() {
      return this.form.tasks.length
    },

    totalWeight() {
      return this.form.tasks.reduce(function (accumulator, current) {
        return accumulator + (current.weight || 0)
      }, 0)
    },

    maxWeight() {
      return 100
    }
  },
  async created() {
    const id = this.projectId
    if (!id) {
      alert(this.$t('Something went wrong!'))
      this.$router.push({ name: 'project-processes.index' })
    } else {
      const params = {
        _include: [
          'tasks.user'
        ]
      }
      const { project } = await this.getProject({ id, params })

      // Check user
      if (this.userId !== project.user_id) {
        this.$router.replace({ name: 'error.403' })
      }

      const tasks = project.tasks.map(function(task) {
        return {
          id: task.id,
          name: task.name,
          status: task.status,
          due_date: task.due_date,
          is_first: task.is_first,
          weight: task.weight,
          user: {
            name: task.user.name
          }
        }
      })

      this.$set(this.form, 'tasks', tasks)

      this.disabled = false
    }
  },
  methods: {
    getProject: call('project/show'),
    hideAlert: call('alert/hide'),
    loading: call('app/setLoading'),
    handleSubmit: call('project/complete'),
    utcToLocalDateTimeLess,
    dateFormat,

    async submit(e) {
      e.preventDefault()
      if (!this.canSubmit) {
        alert(this.$t('All tasks should be reviewed before submit.'))
      } else {
        const formValid = this.$refs.formProject.validate()
        if (!formValid || (parseInt(this.totalWeight) !== parseInt(this.maxWeight))) {
          alert(this.$t('Please check data before submit!'))
        } else {
          this.disabled = true
          this.hideAlert()
          this.loading(true)
          const status = await this.handleSubmit({
            id: this.projectId,
            ...this.form
          })
          this.loading(false)
          if (!status) {
            this.disabled = false
          } else {
            this.$router.push({ name: 'project-processes.index' })
          }
        }
      }
    }
  }
}
</script>
